// Which locale numeric dates are formatted in, and which number goes first — the third copy of a
// rule shared with iOS (`TopBarGreetingDate.dateFormattingLocale` / `.localeIsMonthFirst`) and
// Android (`ui/Components.kt`). Keep the three identical.

// The locale numeric dates are formatted in — the BROWSER's language over the ACCOUNT's region.
//
// The browser locale alone gets this wrong for a very ordinary user: someone whose machine is set to
// plain "English (United States)" but whose business is in Israel. There is no separate date-order
// preference to read, so `navigator.language` says `en-US` and every numeric date reads as a US
// date. Reported 2026-08-08, where `08.07` looked like August 7th and meant July 8th.
//
// The connected metasite already knows better and we already fetch it in the same request the UI
// language comes from: Site Properties' `locale.country` is the business's own region — the field
// that also drives its currency and time zone — and it is the region the user actually operates in,
// rather than wherever they happen to be holding the laptop.
//
// LANGUAGE stays the browser's, always. The account's `languageCode` is the site's content language
// and is resolved separately (`resolveInitialLocale`); borrowing it here would let a formatting
// concern change the words on the page. Only the REGION is taken.
//
// `accountCountry` null (not yet fetched, signed out, unset) → the browser locale unchanged, so
// nothing regresses where the two already agree.
export function dateFormattingLocale(device: string | undefined, accountCountry: string | null | undefined): string | undefined {
  // TWO LETTERS, checked — not merely two characters. Android's `Locale.Builder` THROWS on a 2-char
  // non-alpha region, so the same check has to be here or the three surfaces stop agreeing on the one
  // input where they can differ.
  const region = accountCountry?.trim().toUpperCase();
  if (!region || !/^[A-Z]{2}$/.test(region)) return device;
  const language = device?.split('-')[0];
  if (!language) return `und-${region}`;
  return `${language}-${region}`;
}

// Whether `locale` orders the month before the day in an all-numeric date (en-US "M/d" → true;
// en-GB "dd/MM", de-DE "dd.MM." → false). Falls back to day-first when the probe throws or either
// field is absent — the same default both native helpers document.
export function localeIsMonthFirst(locale: string | undefined, now: Date): boolean {
  try {
    const parts = new Intl.DateTimeFormat(locale, { month: 'numeric', day: 'numeric' }).formatToParts(now);
    const m = parts.findIndex((p) => p.type === 'month');
    const d = parts.findIndex((p) => p.type === 'day');
    return m !== -1 && d !== -1 && m < d;
  } catch {
    return false;
  }
}
